import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { cpApi } from '../api/api';

type SepayTransaction = {
  id?: string | number;
  transaction_date?: string;
  amount_in?: string;
  amount_out?: string;
  accumulated?: string;
  transaction_content?: string;
  reference_number?: string;
  sub_account?: string;
  bank_brand_name?: string;
};

type Props = {
  open: boolean;
  profileId: number;
  profileName: string;
  accountNo: string;
  onClose: () => void;
};

function formatMoney(raw: string | number | undefined): string {
  const n = Number(raw ?? 0);
  if (!Number.isFinite(n)) return String(raw ?? '—');
  return n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

function txAmount(tx: SepayTransaction): { text: string; cls: string } {
  const amountIn = Number(tx.amount_in ?? 0);
  const amountOut = Number(tx.amount_out ?? 0);
  if (amountIn > 0) return { text: `+${formatMoney(tx.amount_in)}`, cls: 'text-emerald-700 dark:text-emerald-300' };
  if (amountOut > 0) return { text: `-${formatMoney(tx.amount_out)}`, cls: 'text-rose-700 dark:text-rose-300' };
  return { text: '0', cls: 'text-slate-500' };
}

export function SepayTransactionsButton({
  onClick,
  provider = 'sepay',
}: {
  onClick: () => void;
  provider?: 'sepay' | 'casso';
}) {
  const isCasso = provider === 'casso';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border shadow-sm transition ${
        isCasso
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'
          : 'border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300'
      }`}
      title={isCasso ? 'Casso' : 'SePay'}
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 4h16v4H4z" />
        <path d="M6 8v12h12V8" />
        <path d="M9 13h6" />
        <path d="M12 10v6" />
      </svg>
    </button>
  );
}

export function SepayTransactionsWidget({ open, profileId, profileName, accountNo, onClose }: Props) {
  const [limit, setLimit] = useState(30);
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState<{ transactions?: SepayTransaction[]; total?: number; total_pages?: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalPages = Math.max(1, Number(data?.total_pages ?? 1));
  const transactions = useMemo(() => data?.transactions ?? [], [data]);

  const load = useCallback(async () => {
    if (!open || profileId <= 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await cpApi.getVndQrProfileSepayTransactions(profileId, {
        limit,
        page,
        date_from: dateFrom,
        date_to: dateTo,
      });
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки транзакций');
      setData(null);
    } finally {
      setBusy(false);
    }
  }, [dateFrom, dateTo, limit, open, page, profileId]);

  useEffect(() => {
    if (open) void load();
  }, [load, open]);

  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo, limit]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-slate-950/70 p-3 backdrop-blur-[3px]" onClick={onClose}>
      <div className="flex h-[94vh] w-[96vw] max-w-[1600px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 bg-gradient-to-r from-sky-700 via-blue-800 to-indigo-900 px-5 py-4 text-white dark:border-slate-700">
          <div>
            <h3 className="text-lg font-semibold">Транзакции SePay</h3>
            <p className="mt-1 text-sm text-sky-100">{profileName || `Профиль #${profileId}`} · {accountNo || '—'}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl border border-white/25 bg-white/10 px-3 py-2 text-sm font-semibold text-white transition hover:bg-white/20">
            Закрыть
          </button>
        </div>

        <div className="border-b border-slate-200 bg-slate-50 px-5 py-3 dark:border-slate-700 dark:bg-slate-800/70">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Дата от
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium normal-case text-slate-800 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Дата до
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium normal-case text-slate-800 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Показывать
              <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="mt-1 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium normal-case text-slate-800 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100">
                <option value={30}>30</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
            <button type="button" onClick={() => void load()} disabled={busy} className="rounded-lg border border-sky-300 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-800 transition hover:bg-sky-100 disabled:opacity-60 dark:border-sky-900/50 dark:bg-sky-950/40 dark:text-sky-300">
              Обновить
            </button>
            <div className="ml-auto text-sm text-slate-600 dark:text-slate-300">
              Всего: <span className="font-semibold text-slate-900 dark:text-white">{Number(data?.total ?? 0).toLocaleString('ru-RU')}</span>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {busy ? <div className="py-14 text-center text-sm text-slate-500">Загрузка транзакций...</div> : null}
          {!busy && error ? <div className="m-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-200">{error}</div> : null}
          {!busy && !error && transactions.length === 0 ? (
            <div className="m-5 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-12 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/70">Транзакции не найдены</div>
          ) : null}
          {!busy && !error && transactions.length > 0 ? (
            <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-800">
              <thead className="sticky top-0 bg-white shadow-sm dark:bg-slate-900">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">Дата</th>
                  <th className="px-4 py-3">Сумма</th>
                  <th className="px-4 py-3">Sub account</th>
                  <th className="px-4 py-3">Банк</th>
                  <th className="px-4 py-3">Комментарий</th>
                  <th className="px-4 py-3">Reference</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {transactions.map((tx) => {
                  const amount = txAmount(tx);
                  return (
                    <tr key={String(tx.id)} className="hover:bg-sky-50/45 dark:hover:bg-slate-800/60">
                      <td className="whitespace-nowrap px-4 py-3 font-mono font-semibold text-slate-900 dark:text-white">{tx.id ?? '—'}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-700 dark:text-slate-300">{tx.transaction_date ?? '—'}</td>
                      <td className={`whitespace-nowrap px-4 py-3 font-semibold tabular-nums ${amount.cls}`}>{amount.text}</td>
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-400">{tx.sub_account ?? '—'}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-700 dark:text-slate-300">{tx.bank_brand_name ?? '—'}</td>
                      <td className="max-w-md px-4 py-3 text-slate-700 dark:text-slate-300">{tx.transaction_content ?? '—'}</td>
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-500">{tx.reference_number ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-5 py-3 dark:border-slate-700 dark:bg-slate-800/70">
          <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || busy} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200">
            Назад
          </button>
          <div className="text-sm text-slate-600 dark:text-slate-300">Страница <span className="font-semibold text-slate-900 dark:text-white">{page}</span> из <span className="font-semibold text-slate-900 dark:text-white">{totalPages}</span></div>
          <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || busy} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200">
            Вперёд
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
